import clsx from "clsx";
import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  wrapperClassName?: string;
  placement?: "right" | "top" | "bottom";
}

export function Tooltip({
  content,
  children,
  wrapperClassName,
  placement = "right",
}: TooltipProps): React.JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ "aria-describedby"?: string }>, {
        "aria-describedby": open ? id : undefined,
      })
    : children;

  function positionTooltip(): void {
    const wrapper = wrapperRef.current;
    const tooltip = tooltipRef.current;
    if (!wrapper || !tooltip) return;

    const gap = 10;
    const padding = 8;
    const anchorRect = wrapper.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left: number;
    let top: number;

    if (placement === "top" || placement === "bottom") {
      left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      top =
        placement === "top" ? anchorRect.top - tooltipRect.height - gap : anchorRect.bottom + gap;

      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));

      if (placement === "top" && top < padding) {
        top = Math.min(window.innerHeight - tooltipRect.height - padding, anchorRect.bottom + gap);
      }

      if (placement === "bottom" && top + tooltipRect.height > window.innerHeight - padding) {
        top = Math.max(padding, anchorRect.top - tooltipRect.height - gap);
      }
    } else {
      left = anchorRect.right + gap;
      top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;

      if (left + tooltipRect.width > window.innerWidth - padding) {
        left = Math.max(padding, anchorRect.left - gap - tooltipRect.width);
      }

      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  useEffect(() => {
    if (!open) return;
    positionTooltip();
    const handleReposition = () => positionTooltip();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className={clsx("relative", wrapperClassName)}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={() => setOpen(false)}
    >
      {trigger}
      {open ? (
        <div ref={tooltipRef} id={id} role="tooltip" className="pointer-events-none fixed z-50">
          <div className="border-cyber-border bg-cyber-bg text-cyber-text rounded-md border px-2 py-1.5 text-[11px] leading-4 shadow-[0_10px_24px_rgba(0,0,0,0.35)]">
            {content}
          </div>
        </div>
      ) : null}
    </div>
  );
}
